import { useState, useEffect } from 'react';
import useMqttSubscribe from '@hooks/useMqttSubscribe';
import { useMqttStore } from '@stores/mqttStore';

export type CellMeasurement = {
  testPoint: string;
  measured: number | null;
  unit: string | null;
};

export type CellStatus = {
  cell: string;
  equipmentGlobalId: string | null;
  status: 'pending' | 'running' | 'completed' | 'failed';
  lastMeasurement: CellMeasurement | null;
};

type ItemStatusMessage = {
  cell?: string | null;
  equipmentGlobalId?: string | null;
  status: string;
};

type MeasurementMessage = {
  cell?: string | null;
  testPoint: string;
  measured: number | null;
  unit: string | null;
};

const mapStatus = (status: string | undefined): CellStatus['status'] => {
  switch (status?.toUpperCase()) {
    case 'RUNNING':
      return 'running';
    case 'COMPLETED':
      return 'completed';
    case 'FAILED':
      return 'failed';
    default:
      return 'pending';
  }
};

export const useCellStatuses = (runId: string | null) => {
  const stationId = useMqttStore(s => s.stationId);
  const itemTopic = runId
    ? `voltbot/${stationId}/script/run/${runId}/item/+/status`
    : '';
  const measurementTopic = runId
    ? `voltbot/${stationId}/script/run/${runId}/measurement`
    : '';

  const [cells, setCells] = useState<Map<string, CellStatus>>(new Map());

  const { lastMessage: itemMsg } = useMqttSubscribe<ItemStatusMessage>(
    itemTopic,
    { enabled: !!stationId && !!runId }
  );
  const { lastMessage: measurementMsg } = useMqttSubscribe<MeasurementMessage>(
    measurementTopic,
    { enabled: !!stationId && !!runId }
  );

  useEffect(() => {
    const cell = itemMsg?.cell;
    if (!cell) return;
    setCells(prev => {
      const next = new Map(prev);
      const existing = next.get(cell);
      next.set(cell, {
        cell,
        equipmentGlobalId:
          itemMsg.equipmentGlobalId ?? existing?.equipmentGlobalId ?? null,
        status: mapStatus(itemMsg.status),
        lastMeasurement: existing?.lastMeasurement ?? null
      });
      return next;
    });
  }, [itemMsg]);

  useEffect(() => {
    const cell = measurementMsg?.cell;
    if (!cell || !measurementMsg) return;
    setCells(prev => {
      const next = new Map(prev);
      const existing = next.get(cell);
      next.set(cell, {
        cell,
        equipmentGlobalId: existing?.equipmentGlobalId ?? null,
        status: existing?.status ?? 'running',
        lastMeasurement: {
          testPoint: measurementMsg.testPoint,
          measured: measurementMsg.measured,
          unit: measurementMsg.unit
        }
      });
      return next;
    });
  }, [measurementMsg]);

  return { cells: Array.from(cells.values()) };
};
